import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { 
  FaBrain, 
  FaUsers, 
  FaLaptopCode, 
  FaCity, 
  FaBriefcase, 
  FaChalkboardTeacher,
  FaHandsHelping,
  FaUserFriends,
  FaFlag,
  FaTools,
  FaCheck,
  FaSync,
  FaUserTie,
  FaBuilding,
  FaHeartbeat,
  FaCalendarAlt,
  FaArrowRight,
  FaLightbulb,
  FaStar,
  FaTimes,
  FaCalculator,
  FaComments,
  FaLanguage,
  FaLaptop,
  FaImage
} from 'react-icons/fa';
import SEO from '../../components/SEO';
import { useAppContext } from '../../hooks/useAppContext';
import { Cloudinary } from '@cloudinary/url-gen';
import './pedagogy.css';

const Pedagogy = () => {
  const { t } = useTranslation();
  const { language } = useAppContext();
  const isArabic = language === 'ar';
  
  // State for interactive elements
  const [activeAxis, setActiveAxis] = useState(null);
  const [activeRole, setActiveRole] = useState(null);
  const [activeSkill, setActiveSkill] = useState(null);
  const cld = new Cloudinary({
    cloud: {
      cloudName: 'dozvy3xru'
    }
  });

  // URL des vidéos
  const videoApropos = cld.video('emqouvxkvagez2yekxim.mp4');

  // Axes data with icons
  const axes = [
    {
      id: 1,
      icon: <FaBriefcase />,
      title: t('pedagogy.axes.axis5.title'),
      description: t('pedagogy.axes.axis5.description'),
      details: t('pedagogy.axes.axis5.details', { returnObjects: true }),
      color: '#0f4c81'
    },
    {
      id: 2,
      icon: <FaUsers />,
      title: t('pedagogy.axes.axis2.title'),
      description: t('pedagogy.axes.axis2.description'),
      details: t('pedagogy.axes.axis2.details', { returnObjects: true }),
      color: '#1a6baa'
    },
    {
      id: 3,
      icon: <FaLaptopCode />,
      title: t('pedagogy.axes.axis3.title'),
      description: t('pedagogy.axes.axis3.description'),
      details: [
        "Calcul et raisonnement logique",
        "Communication écrite et orale",
        "Langues étrangères",
        "Compétences numériques"
      ],
      color: '#2589d0'
    },
    {
      id: 4,
      icon: <FaCity />,
      title: t('pedagogy.axes.axis4.title'),
      description: t('pedagogy.axes.axis4.description'),
      details: t('pedagogy.axes.axis4.details', { returnObjects: true }),
      color: '#3aa0e9'
    },
    {
      id: 5,
      icon: <FaBrain />,
      title: t('pedagogy.axes.axis1.title'),
      description: t('pedagogy.axes.axis1.description'),
      details: t('pedagogy.axes.axis1.details', { returnObjects: true }),
      color: '#4fb3f6'
    }
  ];

  // Détails des compétences clés (axe 3)
  const keySkillsDetails = [
    {
      id: 1,
      title: "Calcul et raisonnement logique",
      icon: <FaCalculator />,
      description: "Développement des capacités de calcul, d'analyse et de résolution de problèmes logiques pour renforcer l'esprit critique et analytique."
    },
    {
      id: 2,
      title: "Communication écrite et orale",
      icon: <FaComments />,
      description: "Perfectionnement des compétences en expression écrite et orale, techniques de présentation et capacité à communiquer efficacement dans différents contextes."
    },
    {
      id: 3,
      title: "Langues étrangères",
      icon: <FaLanguage />,
      description: "Apprentissage et renforcement des compétences linguistiques, principalement en anglais et autres langues selon les besoins, pour favoriser l'ouverture internationale."
    },
    {
      id: 4,
      title: "Compétences numériques",
      icon: <FaLaptop />,
      description: "Maîtrise des outils informatiques essentiels, compétences en bureautique, navigation internet et utilisation des technologies numériques dans un contexte professionnel."
    }
  ];

  // Journey steps data - now 4 steps
  const journeySteps = [
    {
      id: 1,
      icon: <FaFlag />,
      title: t('pedagogy.journey.step1.title'),
      description: t('pedagogy.journey.step1.description'),
      details: t('pedagogy.journey.step1.details', { returnObjects: true }),
      image: "ped3.jpeg"
    },
    {
      id: 2,
      icon: <FaTools />,
      title: t('pedagogy.journey.step2.title'),
      description: t('pedagogy.journey.step2.description'),
      details: t('pedagogy.journey.step2.details', { returnObjects: true }),
      image: "ped5.jpg"
    },
    {
      id: 3,
      icon: <FaCheck />,
      title: t('pedagogy.journey.step3.title'),
      description: t('pedagogy.journey.step3.description'),
      details: t('pedagogy.journey.step3.details', { returnObjects: true }),
      image: "ped2.jpg"
    },
    {
      id: 4,
      icon: <FaSync />,
      title: t('pedagogy.journey.step4.title'),
      description: t('pedagogy.journey.step4.description'),
      details: t('pedagogy.journey.step4.details', { returnObjects: true }),
      image: "ped1.jpg"
    }
  ];

  // Roles data
  const roles = [
    {
      id: 1,
      icon: <FaChalkboardTeacher />,
      title: t('pedagogy.roles.role1.title'),
      description: t('pedagogy.roles.role1.description'),
      details: t('pedagogy.roles.role1.details', { returnObjects: true }),
      color: '#0f4c81'
    },
    {
      id: 2,
      icon: <FaBuilding />,
      title: t('pedagogy.roles.role2.title'),
      description: t('pedagogy.roles.role2.description'),
      details: t('pedagogy.roles.role2.details', { returnObjects: true }),
      color: '#1a6baa'
    },
    {
      id: 3,
      icon: <FaHeartbeat />,
      title: t('pedagogy.roles.role3.title'),
      description: t('pedagogy.roles.role3.description'),
      details: t('pedagogy.roles.role3.details', { returnObjects: true }),
      color: '#2589d0'
    },
    {
      id: 4,
      icon: <FaCalendarAlt />,
      title: t('pedagogy.roles.role4.title'),
      description: t('pedagogy.roles.role4.description'),
      details: t('pedagogy.roles.role4.details', { returnObjects: true }),
      color: '#3aa0e9'
    }
  ];

  // Essential skills
  const essentialSkills = [
    {
      id: 1,
      icon: <FaUserFriends />,
      title: t('pedagogy.skills.skill1.title'),
      description: t('pedagogy.skills.skill1.description')
    },
    {
      id: 2,
      icon: <FaUsers />,
      title: t('pedagogy.skills.skill2.title'),
      description: t('pedagogy.skills.skill2.description')
    },
    {
      id: 3,
      icon: <FaLightbulb />,
      title: t('pedagogy.skills.skill3.title'),
      description: t('pedagogy.skills.skill3.description')
    }
  ];

  // Compétences détaillées pour l'interaction
  const detailedSkills = [
    {
      id: 1,
      title: "Calcul et raisonnement logique",
      icon: <FaCalculator />,
      description: "Développement des capacités de calcul, d'analyse et de résolution de problèmes logiques pour renforcer l'esprit critique et analytique."
    },
    {
      id: 2,
      title: "Communication écrite et orale",
      icon: <FaComments />,
      description: "Perfectionnement des compétences en expression écrite et orale, techniques de présentation et capacité à communiquer efficacement dans différents contextes."
    },
    {
      id: 3,
      title: "Langues étrangères",
      icon: <FaLanguage />,
      description: "Apprentissage et renforcement des compétences linguistiques, principalement en anglais et autres langues selon les besoins, pour favoriser l'ouverture internationale."
    },
    {
      id: 4,
      title: "Compétences numériques",
      icon: <FaLaptop />,
      description: "Maîtrise des outils informatiques essentiels, compétences en bureautique, navigation internet et utilisation des technologies numériques dans un contexte professionnel."
    }
  ];

  // Toggle axis details
  const toggleAxis = (id) => {
    if (activeAxis === id) {
      setActiveAxis(null);
    } else {
      setActiveAxis(id);
    }
  };

  // Toggle role details
  const toggleRole = (id) => {
    if (activeRole === id) {
      setActiveRole(null);
    } else {
      setActiveRole(id);
    }
  };

  // Toggle skill details
  const toggleSkill = (id) => {
    if (activeSkill === id) {
      setActiveSkill(null);
    } else {
      setActiveSkill(id);
    }
  };

  return (
    <div className={`pedagogy-page ${isArabic ? 'rtl' : ''}`}>
      <SEO 
        title={isArabic ? 'المنهجية التربوية - DNC تونس' : 'Pédagogie - Dispositif National Nouvelle Chance'}
        description={isArabic ? 'اكتشف منهجيتنا التربوية المبتكرة لدعم الشباب المنقطعين عن الدراسة في تونس' : 'Découvrez notre approche pédagogique innovante pour accompagner les jeunes déscolarisés en Tunisie'}
        url="/pedagogy"
        type="website"
      />
      {/* Hero Section */}
      <section className="hero-section relative h-screen flex items-center justify-center overflow-hidden">
        <video
          autoPlay
          muted
          loop
          className="absolute w-full h-full object-cover"
        >
          <source src={videoApropos.toURL()} type="video/mp4" />
          Votre navigateur ne supporte pas la lecture de vidéos.
        </video>
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">{t('pedagogy.hero.title')}</h1>
          <p className="text-xl md:text-2xl text-white">{t('pedagogy.hero.description')}</p>
        </div>
        </section>

      {/* Introduction Section */}
      <section className="intro-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{t('pedagogy.intro.title')}</h2>
            <p className="section-subtitle">{t('pedagogy.intro.subtitle')}</p>
          </div>
          <div className="intro-content">
            <div className="intro-text-container">
              <div className="intro-card">
                <h3 className="intro-title">{t('pedagogy.intro.cardTitle')}</h3>
                <p className="intro-text">{t('pedagogy.intro.cardText')}</p>
                <div className="intro-highlight">
                  <span className="highlight-icon"><FaStar /></span>
                  <p>{t('pedagogy.intro.highlight')}</p>
                </div>
              </div>
            </div>
            <div className="intro-image-container">
              <img src={process.env.PUBLIC_URL + "/image/44.png"} alt="Pédagogie DNC" className="intro-image" />
            </div>
          </div>
        </div>
      </section>

      {/* Active Pedagogy Section */}
      <section className="active-pedagogy-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{t('pedagogy.activeMethod.title')}</h2>
            <p className="section-subtitle">{t('pedagogy.activeMethod.subtitle')}</p>
          </div>
          
          <div className="active-pedagogy-image-container">
            <img src={process.env.PUBLIC_URL + "/image/ped5.jpeg"} alt="Pédagogie active" className="active-pedagogy-image" />
          </div>
          
          <motion.div 
            className="active-pedagogy-description"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <p>{t('pedagogy.activeMethod.description')}</p>
          </motion.div>
      
          
          <div className="benefits-section">
            <h3 className="benefits-title">{t('pedagogy.activeMethod.benefits.title')}</h3>
            
            <div className="benefits-grid">
              <motion.div 
                className="benefit-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <div className="benefit-icon">
                  <i className="fas fa-rocket"></i>
                </div>
                <h4>{t('pedagogy.activeMethod.benefits.item1.title')}</h4>
                <p>{t('pedagogy.activeMethod.benefits.item1.description')}</p>
              </motion.div>
              
              <motion.div 
                className="benefit-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <div className="benefit-icon">
                  <i className="fas fa-user-shield"></i>
                </div>
                <h4>{t('pedagogy.activeMethod.benefits.item2.title')}</h4>
                <p>{t('pedagogy.activeMethod.benefits.item2.description')}</p>
              </motion.div>
              
              <motion.div 
                className="benefit-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                viewport={{ once: true }}
              >
                <div className="benefit-icon">
                  <i className="fas fa-tools"></i>
                </div>
                <h4>{t('pedagogy.activeMethod.benefits.item3.title')}</h4>
                <p>{t('pedagogy.activeMethod.benefits.item3.description')}</p>
              </motion.div>
              
              <motion.div 
                className="benefit-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                viewport={{ once: true }}
              >
                <div className="benefit-icon">
                  <i className="fas fa-heart"></i>
                </div>
                <h4>{t('pedagogy.activeMethod.benefits.item4.title')}</h4>
                <p>{t('pedagogy.activeMethod.benefits.item4.description')}</p>
              </motion.div>
            </div>
  
          </div>
        </div>
      </section>

      {/* Journey Section - 4 Steps */}
      <section className="journey-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{t('pedagogy.journey.title')}</h2>
            <p className="section-subtitle">{t('pedagogy.journey.subtitle')}</p>
          </div>
          <div className="journey-cards">
            {journeySteps.map((step) => (
              <div className="journey-card" key={step.id}>
                <div className="journey-card-header">
                  <div className="journey-card-icon">{step.icon}</div>
                  <h3 className="journey-card-title">{step.title}</h3>
                </div>
                <div className="journey-card-content">
                  <div className="journey-card-text">
                    <p className="journey-card-description">{step.description}</p>
                    <ul className="journey-card-details">
                      {step.details.map((detail, index) => (
                        <li key={index}>{detail}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="journey-card-image-container">
                    <img 
                      src={process.env.PUBLIC_URL + "/image/" + step.image} 
                      alt={`Phase ${step.id}`} 
                      className="journey-card-image" 
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Roles Section */}
      <section className="roles-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{t('pedagogy.roles.title')}</h2>
            <p className="section-subtitle">{t('pedagogy.roles.subtitle')}</p>
          </div>
          <div className="roles-grid">
            {roles.map((role) => (
              <div 
                className={`role-card ${activeRole === role.id ? 'active' : ''}`}
                key={role.id}
                onClick={() => toggleRole(role.id)}
              >
                <div className="role-header" style={{ background: role.color }}>
                  <span className="role-icon">{role.icon}</span>
                  <h3 className="role-title">{role.title}</h3>
                </div>
                <div className="role-body">
                  <p className="role-description">{role.description}</p>
                  {activeRole === role.id && (
                    <div className="role-details">
                      <ul>
                        {role.details.map((detail, index) => (
                          <li key={index}>{detail}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Axes Section */}
      <section className="axes-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{t('pedagogy.axes.title')}</h2>
            <p className="section-subtitle">{t('pedagogy.axes.subtitle')}</p>
          </div>
            
          <div className="axes-grid">
            {axes.map((axis) => (
              <div 
                className={`axis-card ${activeAxis === axis.id ? 'active' : ''}`}
                key={axis.id}
                onClick={() => toggleAxis(axis.id)}
              >
                <div className="axis-header" style={{ background: axis.color }}>
                  <div className="axis-number">{axis.id}</div>
                  <span className="axis-icon">{axis.icon}</span>
                  <h3 className="axis-title">{axis.title}</h3>
                </div>
                <div className="axis-body">
                  <p className="axis-description">{axis.description}</p>
                  <span className="axis-expand">
                    {activeAxis === axis.id ? t('about.common.less') : t('about.common.more')}
                  </span>
                </div>
                {activeAxis === axis.id && (
                  <div className="axis-details">
                    <table className="axis-table">
                      <thead>
                        <tr>
                          <th>{t('pedagogy.axes.tableHeader')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {axis.details.map((detail, index) => (
                          <tr key={index}>
                            <td>{detail}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    
                    {axis.id === 3 && (
                      <div className="axis-skill-details">
                        
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section className="skills-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{t('pedagogy.skills.title')}</h2>
            <p className="section-subtitle">{t('pedagogy.skills.subtitle')}</p>
          </div>
          <div className="skills-banner">
            <img src={process.env.PUBLIC_URL + "/image/hero/image8.JPG"} alt="Compétences essentielles" className="skills-banner-image" />
          </div>
          <div className="skills-grid">
            {essentialSkills.map((skill) => (
              <div 
                className="skill-card"
                key={skill.id}
              >
                <span className="skill-icon">{skill.icon}</span>
                <h3 className="skill-title">{skill.title}</h3>
                <p className="skill-description">{skill.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-video-background">
          <video
            autoPlay
            muted
            loop
            playsInline
            className="cta-video"
          >
            <source src="/video/cta.mp4" type="video/mp4" />
          </video>
          <div className="cta-overlay"></div>
        </div>
        <motion.div 
          className="container cta-content"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="cta-title">{t('pedagogy.cta.title')}</h2>
          <p className="cta-text">{t('pedagogy.cta.text')}</p>
          <a href="/registration" className="cta-button">{t('pedagogy.cta.button')}</a>
        </motion.div>
      </section>
      
     
    </div>
  );
};

export default Pedagogy;
